import React from 'react';
import { Button } from '@/shared/components/ui/button';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Opcional: Enviar erro para serviço de monitoramento
    // logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='min-h-screen flex items-center justify-center bg-gray-50'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center'>
              <svg
                className='h-8 w-8 text-red-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
            </div>

            <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2'>
              Oops! Algo deu errado
            </h2>
            <p className='text-gray-600 dark:text-white mb-6'>
              Ocorreu um erro inesperado. Por favor, recarregue a página ou tente novamente.
            </p>

            <div className='space-y-3'>
              <Button onClick={() => window.location.reload()} className='w-full'>
                Recarregar Página
              </Button>

              <Button
                onClick={() => (window.location.href = '/')}
                variant='outline'
                className='w-full'
              >
                Ir para Home
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <details className='mt-6 text-left'>
                <summary className='cursor-pointer text-sm text-gray-500 dark:text-white hover:text-gray-700 dark:text-gray-300 dark:text-gray-300'>
                  Detalhes do Erro (Dev)
                </summary>
                <div className='mt-2 p-3 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-40'>
                  <div className='mb-2'>
                    <strong>Error:</strong>
                    <pre className='whitespace-pre-wrap'>{this.state.error?.toString()}</pre>
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className='whitespace-pre-wrap'>
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
